/**
 * Fetches product industry metafields in English (default locale) via Storefront API
 * and updates image URLs to use English slugs
 */
class IndustryLocaleHandler {
  constructor() {
    this.cache = this.loadCache();
    this.init();
  }

  loadCache() {
    try {
      const cached = localStorage.getItem('industryEnglishNames');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      console.error('Error loading cache:', e);
      return {};
    }
  }

  saveCache() {
    try {
      localStorage.setItem('industryEnglishNames', JSON.stringify(this.cache));
    } catch (e) {
      console.error('Error saving cache:', e);
    }
  }

  async init() {
    // Check if we have the necessary config
    if (!window.Shopify?.storefrontAccessToken) {
      console.error('Storefront API access token not found');
      return;
    }

    // Process initial products
    await this.processVisibleProducts();

    // Set up MutationObserver to watch for dynamically added products
    this.observeProductChanges();
  }

  async processVisibleProducts() {
    const industryTags = document.querySelectorAll('.product-industry-tag');
    if (!industryTags.length) return;

    // Group by product ID to batch fetch
    // Always collect ALL visible products, even if previously processed
    // because they may have been re-rendered by filters
    const productIds = new Set();
    industryTags.forEach(tag => {
      const productCard = tag.closest('[data-product-id]');
      if (productCard) {
        productIds.add(productCard.dataset.productId);
      }
    });

    if (productIds.size === 0) return;

    // Fetch all products in a single batch request
    const productIdsArray = Array.from(productIds);
    await this.updateProductsIndustries(productIdsArray);
  }

  observeProductChanges() {
    // Watch for new products being added to the page (e.g., from filters)
    const observer = new MutationObserver((mutations) => {
      let hasNewProducts = false;
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1 && (
            node.matches?.('[data-product-id]') ||
            node.querySelector?.('[data-product-id]')
          )) {
            hasNewProducts = true;
          }
        });
      });

      if (hasNewProducts) {
        this.processVisibleProducts();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  async updateProductsIndustries(productIds) {
    try {
      // Check which products need to be fetched from API (not in cache)
      const productsToFetch = productIds.filter(id => !this.cache[id]);

      if (productsToFetch.length > 0) {
        // Fetch all products in a single request
        const productsData = await this.fetchMultipleProductsIndustries(productsToFetch);

        if (productsData) {
          // Store in cache
          productsData.forEach(({ productId, industries }) => {
            this.cache[productId] = industries;
          });

          // Save to localStorage
          this.saveCache();
        }
      }

      // Update images for all products (using cache)
      productIds.forEach(productId => {
        const industries = this.cache[productId];

        if (!industries || !industries.length) {
          // Don't update this product - leave it with the Liquid-generated image URL
          return;
        }

        const productCard = document.querySelector(`[data-product-id="${productId}"]`);
        if (!productCard) return;

        const industryTags = productCard.querySelectorAll('.product-industry-tag');
        industryTags.forEach((tag, index) => {
          const img = tag.querySelector('.industry-icon');
          if (img && industries[index]) {
            // Only update if the image is still in loading state or has wrong src
            const isLoading = img.classList.contains('industry-icon-loading');
            const slug = this.handleize(industries[index]);
            const expectedSlug = `ico_${slug}.svg`;

            // Check if the current src has the wrong slug
            const needsUpdate = isLoading || !img.src.includes(expectedSlug);

            if (needsUpdate) {
              // Get the template URL from data-src and replace the filename
              const templateSrc = img.getAttribute('data-src') || img.src;
              const newSrc = templateSrc.replace(/ico_[^.]+\.svg/, expectedSlug);

              // Set the src and remove loading class
              img.src = newSrc;
              img.classList.remove('industry-icon-loading');
            }
          }
        });
      });
    } catch (error) {
      console.error('Error updating product industries:', error);
    }
  }

  async fetchMultipleProductsIndustries(productIds) {
    // Build GraphQL IDs
    const ids = productIds.map(id => `gid://shopify/Product/${id}`);

    const query = `
      query getProductsIndustries($ids: [ID!]!) @inContext(language: EN) {
        nodes(ids: $ids) {
          ... on Product {
            id
            metafield(namespace: "custom", key: "industry") {
              value
            }
          }
        }
      }
    `;

    const variables = { ids };

    // Storefront API endpoint - must use the actual Shopify domain
    const apiUrl = `https://${window.Shopify.shop}/api/2025-01/graphql.json`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.Shopify.storefrontAccessToken
      },
      body: JSON.stringify({ query, variables })
    });

    if (!response.ok) {
      console.error('Industry locale API request failed:', response.status, response.statusText);
      return null;
    }

    const data = await response.json();
    if (data.errors) {
      console.error('Industry locale GraphQL errors:', data.errors);
      return null;
    }

    if (!data.data?.nodes) return null;

    // Parse and return results
    const results = data.data.nodes.map(node => {
      if (!node?.id) return null;

      const productId = node.id.split('/').pop();
      const metafieldValue = node.metafield?.value;

      let industries = [];
      if (metafieldValue) {
        try {
          industries = JSON.parse(metafieldValue);
        } catch (e) {
          industries = [metafieldValue];
        }
      }

      return { productId, industries };
    }).filter(item => item && item.industries.length > 0);

    return results;
  }

  handleize(str) {
    return str
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new IndustryLocaleHandler());
} else {
  new IndustryLocaleHandler();
}
